/* eslint-disable */
import axios from 'axios';
import {
  AML_VERIFIED_DATA,
  CREDIT_REPORT_VERIFIED_DATA,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
  GET_LINKED_ONBOARDING_FORMS_API,
  GET_QUESTION_MAPPINGS,
  IDV_VERIFIED_DATA,
  KYC_VERIFIED_DATA,
  GET_VERIFICATION_FORMS,
  GET_VERIFICATION_SERVICE_BINDINGS,
  GET_VERIFICATION_SERVICES,
  RUN_AML_VERIFICATION,
  RUN_CIPC_VERIFICATION,
  RUN_CREDIT_REPORT_VERIFICATION,
  RUN_IDENTITY_VERIFICATION,
  RUN_KYC_VERIFICATION,
  get_request_config
} from '@/utils/system_constants/constant.js';
import {
  first_value,
  normalize_collection,
  normalize_text,
  parse_response_data
} from '@/services/onboarding_data_helpers';
import {
  extract_entity_type_config_payload,
  onboarding_request_cache
} from '@/services/onboarding_journey_service_helpers';
import { clear_manual_verification_cache } from '@/services/onboarding_journey_application_service';

const normalize_verification_service_name = (service) => normalize_text(first_value([
  service?.name,
  service?.service_name,
  service?.title
], ''));

const resolve_auto_verification_endpoint = (service) => {
  const normalized_name = normalize_verification_service_name(service);

  if (normalized_name.includes('credit')) return RUN_CREDIT_REPORT_VERIFICATION;
  if (normalized_name.includes('cipc')) return RUN_CIPC_VERIFICATION;
  if (normalized_name.includes('aml')) return RUN_AML_VERIFICATION;
  if (normalized_name.includes('identity') || normalized_name.includes('id verification') || normalized_name.includes('idv')) {
    return RUN_IDENTITY_VERIFICATION;
  }
  if (normalized_name.includes('kyc')) return RUN_KYC_VERIFICATION;

  return '';
};

const get_application_form_instance_id = (form) => Number(first_value([
  form?.application_form_id,
  form?.id
], 0)) || 0;

const get_application_form_definition_id = (form) => Number(first_value([
  form?.form_id,
  form?.form?.id,
  form?.form?.form_id
], 0)) || 0;

const normalize_verification_forms = (response) => normalize_collection(response)
  .map((form) => ({
    ...form,
    verification_form_id: Number(first_value([
      form?.verification_form_id,
      form?.id
    ], 0)) || 0,
    verification_form_definition_id: Number(first_value([
      form?.survey_form_id,
      form?.form_id,
      form?.verification_form_definition_id,
      form?.form?.id
    ], 0)) || 0
  }))
  .filter((form) => form.verification_form_id > 0);

const normalize_linked_onboarding_forms = (response) => normalize_collection(response)
  .map((link) => ({
    ...link,
    onboarding_form_id: Number(first_value([
      link?.onboarding_form_id,
      link?.onboarding_form?.id
    ], 0)) || 0,
    verification_form_id: Number(first_value([
      link?.verification_form_id,
      link?.verification_form,
      link?.id
    ], 0)) || 0
  }))
  .filter((link) => link.onboarding_form_id > 0 && link.verification_form_id > 0);

const EMPTY_VERIFICATION_BINDINGS = {
  entity_config_id: null,
  service_ids: []
};

function normalize_verification_service_bindings(response) {
  return normalize_collection(response).filter((binding) => binding?.is_active !== false);
}

async function fetch_verification_service_bindings_for_config(entity_config_id) {
  const resolved_entity_config_id = Number(entity_config_id || 0);
  if (resolved_entity_config_id <= 0) return [];

  const response = await axios.get(
    GET_VERIFICATION_SERVICE_BINDINGS,
    get_request_config({
      params: { entity_config_id: resolved_entity_config_id }
    })
  );

  return normalize_verification_service_bindings(response);
}

const build_linked_forms_request = (verification_form_id) => axios.get(
  GET_LINKED_ONBOARDING_FORMS_API,
  get_request_config({
    params: { verification_form_id }
  })
);

const build_question_mappings_request = (verification_form_id, service_id) => axios.get(
  GET_QUESTION_MAPPINGS,
  get_request_config({
    params: {
      service_id,
      verification_form_id
    }
  })
);

const resolve_linked_forms_for_verification_form = async (verification_form, service_id) => {
  const verification_form_id = verification_form?.verification_form_id;
  if (!verification_form_id || !service_id) return [];

  return Promise.all([
    build_linked_forms_request(verification_form_id),
    build_question_mappings_request(verification_form_id, service_id)
  ])
    .then(([linked_forms_response, mappings_response]) => {
      const mappings = parse_response_data(mappings_response)?.mappings || [];
      return Array.isArray(mappings) && mappings.length
        ? normalize_linked_onboarding_forms(linked_forms_response)
        : [];
    })
    .catch(() => []);
};

export const fetch_onboarding_services = async () => {
  const cache_key = 'all';
  if (onboarding_request_cache.verification_services.has(cache_key)) {
    return onboarding_request_cache.verification_services.get(cache_key);
  }

  const request = axios.get(GET_VERIFICATION_SERVICES, get_request_config())
    .then((response) => normalize_collection(response)
    .map((service) => ({
      ...service,
      id: first_value([service?.id, service?.service_id], null),
      name: first_value([service?.name, service?.service_name], 'Verification service'),
      description: first_value([service?.description], '')
    }))
    .filter((service) => service.id))
    .catch((error) => {
      onboarding_request_cache.verification_services.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.verification_services.set(cache_key, request);
  return request;
};

export const fetch_latest_kyc_result = async (entity_profile_id) => {
  const resolved_entity_profile_id = Number(entity_profile_id || 0);
  if (resolved_entity_profile_id <= 0) return null;

  const response = await axios.get(
    KYC_VERIFIED_DATA,
    get_request_config({
      params: { entity_profile_id: resolved_entity_profile_id }
    })
  );

  const payload = parse_response_data(response);
  return payload && typeof payload === 'object' ? payload : null;
};

export const fetch_aml_result = async (entity_profile_id) => {
  const resolved_entity_profile_id = Number(entity_profile_id || 0);
  if (resolved_entity_profile_id <= 0) return null;

  const response = await axios.get(
    AML_VERIFIED_DATA,
    get_request_config({
      params: { entity_profile_id: resolved_entity_profile_id }
    })
  );

  const payload = parse_response_data(response);
  return payload && typeof payload === 'object' ? payload : null;
};

export const fetch_idv_result = async (entity_profile_id, service_id = null) => {
  const resolved_entity_profile_id = Number(entity_profile_id || 0);
  const resolved_service_id = Number(service_id || 0);
  if (resolved_entity_profile_id <= 0) return null;

  const response = await axios.get(
    IDV_VERIFIED_DATA,
    get_request_config({
      params: {
        entity_profile_id: resolved_entity_profile_id,
        ...(resolved_service_id > 0 ? { service_id: resolved_service_id } : {})
      }
    })
  );

  const payload = parse_response_data(response);
  return payload && typeof payload === 'object' ? payload : null;
};

export const fetch_credit_report_result = async (entity_profile_id) => {
  const resolved_entity_profile_id = Number(entity_profile_id || 0);
  if (resolved_entity_profile_id <= 0) return null;

  const response = await axios.get(
    CREDIT_REPORT_VERIFIED_DATA,
    get_request_config({
      params: { entity_profile_id: resolved_entity_profile_id }
    })
  );

  const payload = parse_response_data(response);
  return payload && typeof payload === 'object' ? payload : null;
};

function build_verification_binding_params(entity_type_id, options = {}) {
  const resolved_entity_type_id = Number(entity_type_id || 0);
  const sub_entity_type_id = resolve_optional_number(options?.sub_entity_type_id);
  const project_id = resolve_optional_number(options?.project_id);

  return {
    entity_type_id: resolved_entity_type_id,
    params: build_verification_binding_request_params(resolved_entity_type_id, sub_entity_type_id, project_id),
    cache_key: JSON.stringify({ entity_type_id: resolved_entity_type_id, sub_entity_type_id, project_id })
  };
}

function resolve_optional_number(value) {
  return Number(value || 0) || null;
}

function build_verification_binding_request_params(entity_type_id, sub_entity_type_id, project_id) {
  return {
    entity_type_id,
    ...(sub_entity_type_id ? { sub_entity_type_id } : {}),
    ...(project_id ? { project_id } : {})
  };
}

function extract_service_id(binding) {
  return Number(first_value([
    binding?.service_id,
    binding?.service,
    binding?.verification_service_id,
    binding?.verification_service
  ], 0));
}

function build_bindings_payload({ entity_config_id, verification_bindings, manual_verification_bindings }) {
  const service_ids = verification_bindings
    .map(extract_service_id)
    .filter((service_id) => Number.isFinite(service_id) && service_id > 0);
  const manual_bindings = manual_verification_bindings.filter((binding) => binding?.is_active !== false);

  return Object.assign({}, {
    entity_config_id,
    service_ids,
    manual_bindings
  });
}

async function resolve_verification_bindings_payload(response) {
  const config_payload = extract_entity_type_config_payload(response);
  const embedded_verification_bindings = Array.isArray(config_payload?.verification_service_bindings)
    ? config_payload.verification_service_bindings
    : [];
  const manual_verification_bindings = Array.isArray(config_payload?.manual_verification_bindings)
    ? config_payload.manual_verification_bindings
    : [];
  const entity_config_id = first_value([
    config_payload?.entity_config?.id,
    config_payload?.entity_config_id
  ], null);
  const verification_bindings = embedded_verification_bindings.length
    ? embedded_verification_bindings
    : await fetch_verification_service_bindings_for_config(entity_config_id);

  return build_bindings_payload({
    entity_config_id,
    verification_bindings,
    manual_verification_bindings
  });
}

export async function fetch_entity_type_verification_bindings(entity_type_id, options = {}) {
  const { entity_type_id: resolved_entity_type_id, params, cache_key } = build_verification_binding_params(entity_type_id, options);
  if (resolved_entity_type_id <= 0) return EMPTY_VERIFICATION_BINDINGS;
  if (onboarding_request_cache.entity_type_verification_bindings.has(cache_key)) {
    return onboarding_request_cache.entity_type_verification_bindings.get(cache_key);
  }

  const request = axios.get(
    GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
    get_request_config({ params })
  )
    .then(resolve_verification_bindings_payload)
    .catch((error) => {
      onboarding_request_cache.entity_type_verification_bindings.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.entity_type_verification_bindings.set(cache_key, request);
  return request;
}

export const resolve_runnable_application_forms_for_service = async ({
  forms = [],
  service_id
}) => {
  const resolved_service_id = Number(service_id || 0);
  const application_forms = Array.isArray(forms) ? forms : [];
  if (resolved_service_id <= 0 || !application_forms.length) return [];

  const verification_forms_response = await axios.get(GET_VERIFICATION_FORMS, get_request_config());
  const verification_forms = normalize_verification_forms(verification_forms_response);
  if (!verification_forms.length) return [];

  const linked_form_groups = await Promise.all(
    verification_forms.map((verification_form) =>
      resolve_linked_forms_for_verification_form(verification_form, resolved_service_id)
    )
  );

  const runnable_onboarding_form_ids = new Set(
    linked_form_groups
      .flat()
      .map((link) => Number(link?.onboarding_form_id || 0))
      .filter((form_id) => form_id > 0)
  );
  if (!runnable_onboarding_form_ids.size) return [];

  return application_forms.filter((form) => (
    runnable_onboarding_form_ids.has(get_application_form_definition_id(form))
    && get_application_form_instance_id(form) > 0
  ));
};

export const run_auto_verification = async ({
  service,
  service_id,
  application_form_id = null,
  lead_form_id = null
}) => {
  const resolved_service = service || {};
  const resolved_service_id = Number(first_value([
    service_id,
    resolved_service?.id,
    resolved_service?.service_id
  ], 0));
  const endpoint = resolve_auto_verification_endpoint({
    ...resolved_service,
    id: resolved_service_id
  });

  if (!endpoint) throw new Error('No auto verification endpoint is configured for this service.');
  if (!resolved_service_id) throw new Error('A verification service is required.');
  if (!application_form_id && !lead_form_id) {
    throw new Error('An application form or lead form is required to run verification.');
  }

  const payload = {
    service_id: resolved_service_id
  };

  if (application_form_id) payload.application_form_id = Number(application_form_id);
  if (lead_form_id) payload.lead_form_id = Number(lead_form_id);

  const response = await axios.post(endpoint, payload, get_request_config());
  clear_manual_verification_cache();
  onboarding_request_cache.all_cases.clear();
  return parse_response_data(response);
};
